import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import api from "../lib/apiClient";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Gift, Send, Copy, RefreshCw, Sparkles, Link as LinkIcon, ChevronRight } from "lucide-react";

// Gifts hub: outbound (send 100 cr for $4) + inbound wishlist link.
// Payments are MOCKED — pricing/copy comes from /api/config/gifts so the
// numbers are never hard-coded in two places.
const FALLBACK_CONFIG = {
  outbound_amount_credits: 100,
  outbound_amount_usd: 4,
  rate_credits_per_usd: 25,
  refund_rate_credits_per_usd: 10,
  refund_credits_for_outbound: 40,
  unclaimed_refund_days: 30,
  amount_tiers_usd: [4, 8, 20, 40],
};

const GiftPage = () => {
  const { user, token, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("send");
  const [config, setConfig] = useState(FALLBACK_CONFIG);
  const [revokeId, setRevokeId] = useState(null);  // gift id awaiting confirm

  // ---- Send-gift form state ----
  const [recipientKind, setRecipientKind] = useState("user");
  const [username, setUsername] = useState("");
  const [recipName, setRecipName] = useState("");
  const [recipEmail, setRecipEmail] = useState("");
  const [recipPhone, setRecipPhone] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [lastClaimUrl, setLastClaimUrl] = useState(null);

  // ---- History ----
  const [sent, setSent] = useState([]);
  const [received, setReceived] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // ---- Inbound wishlist ----
  const [wish, setWish] = useState(null); // {slug, headline, message, active, contributions_count, ...}
  const [wishHeadline, setWishHeadline] = useState("");
  const [wishMessage, setWishMessage] = useState("");
  const [savingWish, setSavingWish] = useState(false);

  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const [s, r] = await Promise.all([
        api.get(`/gifts/sent`, authHeaders),
        api.get(`/gifts/received`, authHeaders),
      ]);
      setSent(s.data || []);
      setReceived(r.data || []);
    } catch (e) {
      // silent
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadWish = async () => {
    try {
      const res = await api.get(`/gift-requests/me`, authHeaders);
      setWish(res.data);
      if (res.data) {
        setWishHeadline(res.data.headline || "");
        setWishMessage(res.data.message || "");
      }
    } catch (e) { /* silent */ }
  };

  useEffect(() => { if (token) { loadHistory(); loadWish(); } }, [token]);

  // Pull live gift constants from the backend (single source of truth)
  useEffect(() => {
    api.get(`/config/gifts`).then(r => setConfig(r.data)).catch(() => { /* fallback already applied */ });
  }, []);

  // Pre-fill recipient when arriving from a profile via /gifts?to=username
  useEffect(() => {
    const to = searchParams.get('to');
    if (to) {
      setActiveTab("send");
      setRecipientKind("user");
      setUsername(to.replace(/^@/, ""));
      // Clear the param so a refresh doesn't re-fill
      searchParams.delete('to');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Stripe redirect-back handler (gift checkout). Mirrors DashboardPage.
  useEffect(() => {
    const sid = searchParams.get('payment_session_id');
    const cancelled = searchParams.get('payment_cancelled');
    if (cancelled) {
      toast.info("Gift cancelled — no charge was made.");
      searchParams.delete('payment_cancelled');
      setSearchParams(searchParams, { replace: true });
      return;
    }
    if (!sid || !token) return;
    let stop = false, attempts = 0;
    const poll = async () => {
      if (stop) return;
      attempts += 1;
      try {
        const res = await api.get(`/payments/status/${sid}`, authHeaders);
        const d = res.data;
        if (d.status === 'paid' && d.fulfilled) {
          toast.success("Gift delivered! Recipient credited.");
          await refreshUser?.();
          await loadHistory();
          searchParams.delete('payment_session_id');
          setSearchParams(searchParams, { replace: true });
          setSending(false);
          return;
        }
        if (['expired', 'failed'].includes(d.status)) {
          toast.error(`Gift payment ${d.status}.`);
          searchParams.delete('payment_session_id');
          setSearchParams(searchParams, { replace: true });
          setSending(false);
          return;
        }
        if (attempts < 8) setTimeout(poll, 2000);
      } catch (err) { /* keep retrying */ }
    };
    toast.info("Confirming gift payment with Stripe…");
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('payment_session_id'), token]);

  const handleSend = async (e) => {
    e?.preventDefault?.();
    setSending(true);
    setLastClaimUrl(null);
    try {
      const recipient = recipientKind === "user"
        ? { kind: "user", username: username.trim().replace(/^@/, "") }
        : { kind: "non_user", email: recipEmail.trim(), phone: recipPhone.trim(), name: recipName.trim() };
      const res = await api.post(`/payments/gift/checkout`, {
        recipient, message, origin_url: window.location.origin,
      }, authHeaders);
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start gift checkout");
      setSending(false);
    }
  };

  const handleRevoke = async (giftId) => {
    setRevokeId(null);
    try {
      const res = await api.post(`/gifts/${giftId}/revoke`, {}, authHeaders);
      toast.success(`Refunded ${res.data.refunded_credits} credits`);
      await refreshUser?.();
      await loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not revoke gift");
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Copied to clipboard");
  };

  const handleSaveWish = async (e) => {
    e?.preventDefault?.();
    setSavingWish(true);
    try {
      const res = await api.post(`/gift-requests`, { headline: wishHeadline, message: wishMessage }, authHeaders);
      setWish(res.data);
      toast.success(wish ? "Wishlist updated" : "Wishlist link created!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save wishlist");
    } finally {
      setSavingWish(false);
    }
  };

  const wishUrl = wish ? `${window.location.origin}/gift-for/${wish.slug}` : null;

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black py-6 sm:py-10" data-testid="gift-page">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="flex items-center gap-3 mb-2">
          <Gift className="h-7 w-7 text-pink-400" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Gifts</h1>
        </div>
        <p className="text-gray-400 text-sm sm:text-base mb-6">
          Drop creativity into someone's life — or invite friends &amp; family to do the same for you.
        </p>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-gray-900 border border-gray-800 w-full flex max-w-full overflow-x-auto whitespace-nowrap">
            <TabsTrigger value="send" data-testid="tab-send-gift" className="data-[state=active]:bg-pink-600 flex-1 min-w-0 px-2 sm:px-3 text-xs sm:text-sm">
              <Send className="h-4 w-4 mr-1.5 shrink-0" /> <span className="truncate">Send a gift</span>
            </TabsTrigger>
            <TabsTrigger value="wishlist" data-testid="tab-wishlist" className="data-[state=active]:bg-emerald-600 flex-1 min-w-0 px-2 sm:px-3 text-xs sm:text-sm">
              <Sparkles className="h-4 w-4 mr-1.5 shrink-0" /> <span className="truncate">My wishlist link</span>
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history" className="data-[state=active]:bg-cyan-600 flex-1 min-w-0 px-2 sm:px-3 text-xs sm:text-sm">
              History
            </TabsTrigger>
          </TabsList>

          {/* ---- Send a gift ---- */}
          <TabsContent value="send">
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Gift className="h-5 w-5 text-pink-400" /> Send {config.outbound_amount_credits} credits for ${config.outbound_amount_usd}
                </CardTitle>
                <p className="text-gray-400 text-sm">
                  Help someone explode with creativity. Pick an existing user (instant) or a non-user (we'll mint a magic link you can text/email).
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSend} className="space-y-4">
                  {/* Recipient kind toggle */}
                  <div className="flex gap-2" data-testid="recipient-kind-toggle">
                    <button type="button"
                      onClick={() => setRecipientKind("user")}
                      data-testid="kind-user-btn"
                      className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition ${recipientKind === "user" ? "bg-pink-600 border-pink-500 text-white" : "bg-gray-950 border-gray-800 text-gray-400 hover:border-gray-700"}`}>
                      Existing user
                    </button>
                    <button type="button"
                      onClick={() => setRecipientKind("non_user")}
                      data-testid="kind-non-user-btn"
                      className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition ${recipientKind === "non_user" ? "bg-pink-600 border-pink-500 text-white" : "bg-gray-950 border-gray-800 text-gray-400 hover:border-gray-700"}`}>
                      Friend or family (no account)
                    </button>
                  </div>

                  {recipientKind === "user" ? (
                    <div>
                      <Label htmlFor="gift-username" className="text-gray-300">Recipient @username</Label>
                      <Input
                        id="gift-username"
                        data-testid="gift-username-input"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="creator123"
                        className="bg-gray-950 border-gray-800 text-white mt-1"
                        required
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="gift-name" className="text-gray-300">Their name (optional)</Label>
                        <Input id="gift-name" data-testid="gift-name-input" value={recipName} onChange={(e) => setRecipName(e.target.value)} placeholder="Aunt Lou" className="bg-gray-950 border-gray-800 text-white mt-1" />
                      </div>
                      <div>
                        <Label htmlFor="gift-email" className="text-gray-300">Email <span className="text-pink-400">*</span></Label>
                        <Input id="gift-email" data-testid="gift-email-input" type="email" value={recipEmail} onChange={(e) => setRecipEmail(e.target.value)} placeholder="lou@family.com" className="bg-gray-950 border-gray-800 text-white mt-1" required />
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="gift-phone" className="text-gray-300">Mobile <span className="text-pink-400">*</span> <span className="text-gray-500 text-xs">(anti-abuse, never shown publicly)</span></Label>
                        <Input id="gift-phone" data-testid="gift-phone-input" value={recipPhone} onChange={(e) => setRecipPhone(e.target.value)} placeholder="+1 555 555 0100" className="bg-gray-950 border-gray-800 text-white mt-1" required />
                      </div>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="gift-msg" className="text-gray-300">Personal note (optional)</Label>
                    <Textarea id="gift-msg" data-testid="gift-message-input" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={280} rows={3}
                      placeholder="Saw this and thought of you — go make something wild." className="bg-gray-950 border-gray-800 text-white mt-1" />
                  </div>

                  <div className="bg-gray-950 border border-gray-800 rounded-lg p-3 text-sm text-gray-400">
                    You'll be charged <span className="text-pink-300 font-semibold">${config.outbound_amount_usd}.00</span> for{" "}
                    <span className="text-emerald-300 font-semibold">{config.outbound_amount_credits} credits</span>.
                    {recipientKind === "non_user" && (
                      <> If they don't claim within {config.unclaimed_refund_days} days you'll auto-refund <span className="text-amber-300 font-semibold">{config.refund_credits_for_outbound} credits</span>. You can also revoke early.</>
                    )}
                  </div>

                  <Button type="submit" disabled={sending} data-testid="send-gift-submit"
                    className="w-full bg-pink-600 hover:bg-pink-700 text-white">
                    {sending ? "Sending..." : <><Send className="h-4 w-4 mr-2" /> Send gift · ${config.outbound_amount_usd}</>}
                  </Button>
                </form>

                {lastClaimUrl && (
                  <div className="mt-5 bg-emerald-950/40 border border-emerald-700/50 rounded-lg p-4" data-testid="claim-link-card">
                    <p className="text-emerald-200 text-sm font-semibold flex items-center gap-2">
                      <LinkIcon className="h-4 w-4" /> Magic claim link
                    </p>
                    <p className="text-emerald-300/80 text-xs mt-1">Forward this to your friend — they'll get 100 credits the moment they sign in.</p>
                    <div className="flex gap-2 mt-3">
                      <Input readOnly value={lastClaimUrl} className="bg-gray-950 border-gray-800 text-white text-xs" data-testid="claim-link-url" />
                      <Button type="button" onClick={() => copyLink(lastClaimUrl)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="copy-claim-link-btn">
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---- My wishlist link ---- */}
          <TabsContent value="wishlist">
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-emerald-400" /> Your public credit wishlist
                </CardTitle>
                <p className="text-gray-400 text-sm">
                  Send this link to friends &amp; family who want to fuel your creativity. They pick a tier ($4 / $8 / $20 / $40 / custom) — no signup needed on their side.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={handleSaveWish} className="space-y-4">
                  <div>
                    <Label htmlFor="wish-headline" className="text-gray-300">Headline</Label>
                    <Input id="wish-headline" data-testid="wish-headline-input" value={wishHeadline} onChange={(e) => setWishHeadline(e.target.value)}
                      maxLength={120} placeholder={`Help @${user.username} unlock more creativity!`} className="bg-gray-950 border-gray-800 text-white mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="wish-msg" className="text-gray-300">Personal pitch (optional)</Label>
                    <Textarea id="wish-msg" data-testid="wish-message-input" value={wishMessage} onChange={(e) => setWishMessage(e.target.value)}
                      maxLength={600} rows={4} placeholder="Birthday's coming up — credits over cake, please!" className="bg-gray-950 border-gray-800 text-white mt-1" />
                  </div>
                  <Button type="submit" disabled={savingWish} data-testid="save-wishlist-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    {savingWish ? "Saving..." : (wish ? "Update wishlist" : "Create my wishlist link")}
                  </Button>
                </form>

                {wish && wishUrl && (
                  <div className="bg-emerald-950/30 border border-emerald-800/60 rounded-lg p-4" data-testid="wishlist-share-card">
                    <p className="text-emerald-200 text-sm font-semibold flex items-center gap-2">
                      <LinkIcon className="h-4 w-4" /> Your shareable link
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Input readOnly value={wishUrl} className="bg-gray-950 border-gray-800 text-white text-xs" data-testid="wishlist-url" />
                      <Button type="button" onClick={() => copyLink(wishUrl)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="copy-wishlist-btn">
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Link to={`/gift-for/${wish.slug}`} target="_blank">
                        <Button type="button" variant="outline" className="border-gray-700 text-gray-200" data-testid="preview-wishlist-btn">
                          Preview <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </Link>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mt-4 text-center">
                      <Stat label="Contributions" value={wish.contributions_count || 0} />
                      <Stat label="USD raised" value={`$${(wish.total_received_usd || 0).toFixed(2)}`} />
                      <Stat label="Credits raised" value={wish.total_received_credits || 0} />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---- History ---- */}
          <TabsContent value="history">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-gray-900 border-gray-800">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white text-base">Sent</CardTitle>
                  <Button variant="ghost" size="sm" onClick={loadHistory} className="text-gray-400 hover:text-white">
                    <RefreshCw className={`h-4 w-4 ${loadingHistory ? 'animate-spin' : ''}`} />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3" data-testid="sent-gifts-list">
                  {sent.length === 0 && <p className="text-gray-500 text-sm">No gifts sent yet.</p>}
                  {sent.map((g) => (
                    <SentGiftRow key={g.id} g={g} onRevokeRequest={(id) => setRevokeId(id)} refundCredits={config.refund_credits_for_outbound} />
                  ))}
                </CardContent>
              </Card>
              <Card className="bg-gray-900 border-gray-800">
                <CardHeader>
                  <CardTitle className="text-white text-base">Received</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3" data-testid="received-gifts-list">
                  {received.length === 0 && <p className="text-gray-500 text-sm">No gifts received yet.</p>}
                  {received.map((g) => (
                    <div key={g.id} className="bg-gray-950 border border-gray-800 rounded-lg p-3">
                      <p className="text-emerald-300 text-sm font-semibold">+{g.amount_credits} credits from @{g.sender_username}</p>
                      {g.message && <p className="text-gray-400 text-xs mt-1 italic">"{g.message}"</p>}
                      <p className="text-gray-500 text-[11px] mt-1">{new Date(g.claimed_at || g.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Revoke-confirmation dialog (shadcn) — replaces window.confirm */}
      <AlertDialog open={!!revokeId} onOpenChange={(open) => !open && setRevokeId(null)}>
        <AlertDialogContent className="bg-gray-900 border-gray-800 text-white" data-testid="revoke-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Revoke this gift?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              The recipient won't be able to claim it anymore. We'll refund you{" "}
              <span className="text-amber-300 font-semibold">{config.refund_credits_for_outbound} credits</span>{" "}
              at the standard top-off rate (${config.outbound_amount_usd} → {config.refund_credits_for_outbound} cr).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700" data-testid="revoke-cancel-btn">
              Keep the gift
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleRevoke(revokeId)}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="revoke-confirm-btn"
            >
              Revoke &amp; refund
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="bg-gray-950 border border-gray-800 rounded-lg p-3">
    <p className="text-gray-400 text-xs">{label}</p>
    <p className="text-white text-lg font-semibold">{value}</p>
  </div>
);

const SentGiftRow = ({ g, onRevokeRequest, refundCredits }) => {
  const statusBadge = {
    sent: <Badge className="bg-amber-600/30 text-amber-200 border-amber-700">pending</Badge>,
    claimed: <Badge className="bg-emerald-600/30 text-emerald-200 border-emerald-700">claimed</Badge>,
    expired: <Badge className="bg-gray-700 text-gray-300 border-gray-600">expired (refunded)</Badge>,
    revoked: <Badge className="bg-rose-700/40 text-rose-200 border-rose-700">revoked (refunded)</Badge>,
  }[g.status] || <Badge>{g.status}</Badge>;
  const recipLabel = g.recipient_kind === "user"
    ? `@${g.recipient_username}`
    : (g.recipient_name || g.recipient_email || "non-user");
  return (
    <div className="bg-gray-950 border border-gray-800 rounded-lg p-3" data-testid={`sent-gift-${g.id}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-white text-sm">
          <span className="text-pink-300">{g.amount_credits} cr</span> → {recipLabel}
        </p>
        {statusBadge}
      </div>
      {g.message && <p className="text-gray-400 text-xs mt-1 italic">"{g.message}"</p>}
      <div className="flex items-center justify-between mt-2">
        <p className="text-gray-500 text-[11px]">{new Date(g.created_at).toLocaleString()}</p>
        {g.status === "sent" && g.recipient_kind === "non_user" && (
          <button onClick={() => onRevokeRequest(g.id)} data-testid={`revoke-gift-${g.id}`}
            className="text-amber-400 hover:text-amber-300 text-xs underline">
            Revoke &amp; refund {refundCredits} cr
          </button>
        )}
        {g.status === "expired" && g.refund_credits > 0 && (
          <span className="text-amber-300 text-xs">+{g.refund_credits} cr refunded</span>
        )}
        {g.status === "revoked" && g.refund_credits > 0 && (
          <span className="text-amber-300 text-xs">+{g.refund_credits} cr refunded</span>
        )}
      </div>
    </div>
  );
};

export default GiftPage;
